/**
 * @file        src/modals/ModalsHost.jsx
 * @description Single mount point for every overlay in the app. Listens for
 *              three window events emitted by view code:
 *                - `nh:open-modal`  → `event.detail = { name, payload }`
 *                - `nh:close-modal` → close whatever is open
 *                - `nh:open-auth`   → shorthand for opening AuthModal
 *              The currently-open modal name and its payload live in local
 *              state; only the matched modal is rendered.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-06-22 Claude — Initial. Avoids prop-drilling open/close state by
 *                         keeping modals reachable from any depth via
 *                         `window.dispatchEvent(new CustomEvent('nh:open-modal',
 *                         { detail: { name: 'auth' } }))`.
 */
import { useCallback, useEffect, useState } from 'react';

import AuthModal from './AuthModal.jsx';
import RoomModal from './RoomModal.jsx';
import PhotoGalleryModal from './PhotoGalleryModal.jsx';
import CancellationPolicyModal from './CancellationPolicyModal.jsx';
import FareBreakupModal from './FareBreakupModal.jsx';
import HighlightsModal from './HighlightsModal.jsx';
import InsuranceBenefitsModal from './InsuranceBenefitsModal.jsx';
import BookingInfoModal from './BookingInfoModal.jsx';
import WorldMapModal from './WorldMapModal.jsx';
import GroupQuoteModal from './GroupQuoteModal.jsx';
import VoucherModal from './VoucherModal.jsx';
import FlightLoadingModal from './FlightLoadingModal.jsx';
import FareModal from './FareModal.jsx';
import FareOptionsModal from './FareOptionsModal.jsx';
import BaggageModal from './BaggageModal.jsx';
import PaxModal from './PaxModal.jsx';
import FlightDetailsModal from './FlightDetailsModal.jsx';
import CorporateRegisterModal from './CorporateRegisterModal.jsx';

const REGISTRY = {
  auth: AuthModal,
  corporateRegister: CorporateRegisterModal,
  room: RoomModal,
  photos: PhotoGalleryModal,
  cancellation: CancellationPolicyModal,
  fareBreakup: FareBreakupModal,
  highlights: HighlightsModal,
  insuranceBenefits: InsuranceBenefitsModal,
  bookingInfo: BookingInfoModal,
  worldMap: WorldMapModal,
  groupQuote: GroupQuoteModal,
  voucher: VoucherModal,
  flightLoading: FlightLoadingModal,
  fare: FareModal,
  fareOptions: FareOptionsModal,
  baggage: BaggageModal,
  pax: PaxModal,
  flightDetails: FlightDetailsModal,
};

export default function ModalsHost() {
  const [{ open, payload }, setState] = useState({ open: null, payload: null });

  const close = useCallback(() => setState({ open: null, payload: null }), []);

  useEffect(() => {
    const onOpen = (e) => {
      const name = e?.detail?.name;
      if (!name || !REGISTRY[name]) return;
      setState({ open: name, payload: e?.detail?.payload || null });
    };
    const onOpenAuth = (e) => setState({ open: 'auth', payload: e?.detail?.payload || null });
    const onClose = () => close();

    window.addEventListener('nh:open-modal', onOpen);
    window.addEventListener('nh:open-auth', onOpenAuth);
    window.addEventListener('nh:close-modal', onClose);
    return () => {
      window.removeEventListener('nh:open-modal', onOpen);
      window.removeEventListener('nh:open-auth', onOpenAuth);
      window.removeEventListener('nh:close-modal', onClose);
    };
  }, [close]);

  return (
    <>
      {Object.entries(REGISTRY).map(([name, Cmp]) => (
        <Cmp
          key={name}
          open={open === name}
          onClose={close}
          payload={open === name ? payload : null}
        />
      ))}
    </>
  );
}
